import { Button, Col, Form, InputGroup, Offcanvas, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  IconCircleX,
  IconPlus,
  IconStar,
  IconStarFilled,
  IconTrash,
} from "@tabler/icons-react";
import TimezoneSelect, { allTimezones } from "react-timezone-select";
import "react-tagsinput/react-tagsinput.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { toast } from "sonner";
import Select from "react-select";
import validator from "validator";
import { getCities, getStates, getCountries } from "../Utils/hooks/location";
import { GetRequest, UpdateRF } from "../Utils/hooks/ApiHandler";
import useLocalStorageState from "use-local-storage-state";
import { useNavigate } from "react-router-dom";
import { parsePhoneNumber } from "libphonenumber-js/min";
import { usePostRequest } from "../Utils/hooks/QueryHandler";
import {
  checkDuplicate,
  useDebounce,
  checkCompanyDuplicate,
  customStyles,
} from "../Utils/helpers/commonhelper";
import {
  DateField,
  MultipleOpt,
  PhoneNumberInput,
  SelectOpt,
  Text,
  Time,
} from "./FieldComp";

// Zod schema for form validation
// const optionalUrl = (label) =>
//   z
//     .string()
//     .trim()
//     .nullable()
//     .optional()
//     .refine(
//       (val) => !val || val === "" || !val.includes(" "),
//       `${label} URL cannot contain spaces`
//     )
//     .refine(
//       (val) => !val || val === "" || /^https?:\/\/.+/.test(val),
//       `Invalid ${label} URL`
//     );

const optionalUrl = (label) =>
  z
    .string()
    .trim()
    .optional()
    .nullable()
    .refine(
      (val) => {
        if (!val || val === "") return true; // empty allowed
        if (val.includes(" ")) return false;
        return /^https?:\/\/.+/.test(val);
      },
      { message: `Invalid ${label} URL` }
    );



const companySchema = z.object({
  company_name: z.string().min(1, "Company Name is required"),
  website: optionalUrl("Website"),
  industry: z.string().min(1, "Industry is required"),
  business_type: z.string().optional(),
  leadOwnerId: z.string().min(1, "Lead Owner is Required"),
  employee_size: z.string().optional(),
  address: z.string().optional(),

  countryId: z.string().min(1, "Country is required !"),
  stateId: z.string().min(1, "State is required !"),
  cityId: z.string().min(1, "City is required !"),

  zipcode: z.string().optional(),
  about: z.string().optional(),

  facebook: optionalUrl("Facebook"),
  twitter: optionalUrl("Twitter"),
  instagram: optionalUrl("Instagram"),
  linkedin: optionalUrl("LinkedIn"),

  rating: z.string().optional(),
});
// Email validation function
const isValidEmail = (email) => {
  return validator.isEmail(email);
};

// Mobile number validation function
const isValidMobileNumber = (phone) => {
  const cleanPhone = phone.replace(/\D/g, "");
  return cleanPhone.length === 10;
};

export const CreateCompany = ({
  setHighlightId,
  setReloadKey,
  show,
  setShow,
  contactsModule,
  setRecentCompanyId,
  setContactShow,
}) => {
  const industryOptions = [
    { value: "Technology", label: "Technology" },
    { value: "Healthcare", label: "Healthcare" },
    { value: "Finance", label: "Finance" },
    { value: "Education", label: "Education" },
    { value: "Manufacturing", label: "Manufacturing" },
    { value: "Retail", label: "Retail" },
    { value: "Real Estate", label: "Real Estate" },
    { value: "Construction", label: "Construction" },
    { value: "Transportation", label: "Transportation" },
    { value: "Food & Beverage", label: "Food & Beverage" },
    { value: "Entertainment", label: "Entertainment" },
    { value: "Consulting", label: "Consulting" },
    { value: "Marketing & Advertising", label: "Marketing & Advertising" },
    { value: "Legal Services", label: "Legal Services" },
    { value: "Insurance", label: "Insurance" },
    { value: "Telecommunication", label: "Telecommunication" },
    { value: "Energy", label: "Energy" },
    { value: "Agriculture", label: "Agriculture" },
    { value: "Automotive", label: "Automotive" },
    { value: "Aerospace", label: "Aerospace" },
    { value: "Pharmaceutical", label: "Pharmaceutical" },
    { value: "Banking", label: "Banking" },
    { value: "E-commerce", label: "E-commerce" },
    { value: "Media", label: "Media" },
    { value: "Non-profit", label: "Non-profit" },
    { value: "Government", label: "Government" },
    { value: "Hospitality", label: "Hospitality" },
    { value: "Travel & Tourism", label: "Travel & Tourism" },
    { value: "Sports & Recreation", label: "Sports & Recreation" },
    { value: "Beauty & Wellness", label: "Beauty & Wellness" },
  ];

  const businessTypeOptions = [
    { value: "Sole Proprietorship", label: "Sole Proprietorship" },
    {
      value: "Limited Liability Partnership(LLP)",
      label: "Limited Liability Partnership(LLP)",
    },
    { value: "Private Limited Company", label: "Private Limited Company" },
    { value: "Public Limited Company", label: "Public Limited Company" },
  ];

  const employeeSizeOptions = [
    { value: "1-10", label: "1-10" },
    { value: "11-50", label: "11-50" },
    { value: "51-200", label: "51-200" },
    { value: "201-500", label: "201-500" },
    { value: "501-1000", label: "501-1000" },
    { value: "1001+", label: "1001+" },
  ];

  // State management
  const [hoverRating, setHoverRating] = useState(0);
  const [emails, setEmails] = useState([{ email_type: "Office", email: "" }]);
  const [contacts, setContacts] = useState([
    { country_code: "+91", phone_type: "Mobile", phone: "" },
  ]);
  const [emailErrors, setEmailErrors] = useState([]);
  const [contactErrors, setContactErrors] = useState([]);
  const [emailValidationErrors, setEmailValidationErrors] = useState([]);
  const [contactValidationErrors, setContactValidationErrors] = useState([]);
  const [duplicateEmailMessage, setDuplicateEmailMessage] = useState({});
  const [duplicatePhoneMessage, setDuplicatePhoneMessage] = useState({});
  const [duplicateCompanyMessage, setDuplicateCompanyMessage] = useState(null);
  const debouncedEmail = useDebounce(emails[0]?.email || "", 500);
  const debouncedPhone = useDebounce(contacts[0]?.phone || "", 500);
  const [inputSearch, setInputSearch] = useState("");
  const [search, setSearch] = useState();
  const [offset, setOffset] = useState(0);
  const [activeSection, setActiveSection] = useState("");
  const [allContacts, setAllContacts] = useState([]);
  const [dynamicFields, setDynamicFields] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState({
    value: "Asia/Kolkata",
    label: "(GMT+5:30) Chennai, Kolkata, Mumbai, New Delhi",
    offset: 5.5,
    abbrev: "IST",
    altName: "India Standard Time",
  });
  const [image, setImage] = useState();
  const [leadOwner, setLeadOwner] = useState([]);
  const [country, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [city, setCity] = useState([]);
  const [emptyEmailIndices, setEmptyEmailIndices] = useState([]);
  const [emptyContactIndices, setEmptyContactIndices] = useState([]);

  // NEW STATE: Toggle for showing only required/important fields
  const [showOnlyRequired, setShowOnlyRequired] = useState(true);

  const emailSectionRef = useRef(null);
  const contactSectionRef = useRef(null);

  const [selectedCountry, setSelectedCountry] = useState({
    value: 99,
    label: "India",
  });
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [customer, setCustomer] = useLocalStorageState("customer", {
    token: {},
  });

  // groped state to hold the dynamic group data
  const [grouped, setGrouped] = useState([]);
  // ungroped state to hold the dynamic group data
  const [ungrouped, setUnGrouped] = useState([]);

  const customer_id = customer?.customer_details?.customer_id || 1;
  const token = customer?.token?.authorization || "";
  const rf = customer?.token?.authentication || "";
  const headers = {
    Authorization: token,
    Authentication: rf,
  };
  const navigate = useNavigate();

  // Helper function to check if field should be shown
  const shouldShowField = (field) => {
    if (!showOnlyRequired) return true; // Show all fields when toggle is off

    // When toggle is on, show only fields that are required OR important
    return field.is_required === "yes" || field.is_important === "yes";
  };

  const generateDynamicSchema = (fields) => {
    const schemaFields = {};

    fields.forEach((field) => {
      const fieldId = field.id;

      switch (field.label_type) {
        case "text":
        case "largeText":
        case "address":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)
              : z.string().optional();
          break;

        case "numerical":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)

              : z.string().optional();
          break;

        case "monetary":
          schemaFields[fieldId] = field.is_required === "yes"
            ? z.preprocess(
              (val) => (val === "" || val === null || val === undefined ? undefined : Number(val)),
              z.number({
                required_error: `${field.label_name} is required`,
                invalid_type_error: "Please enter a valid number"
              })
                .min(0.01, "Amount must be positive")
            )
            : z.preprocess(
              (val) => (val === "" || val === null || val === undefined ? null : Number(val)),
              z.number().min(0, "Amount cannot be negative").optional().nullable()
            );
          break;

        case "single-option":
        case "user":
        case "person":
        case "organization":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)
              : z.string().optional().nullable().or(z.literal(""));
          break;

        case "date":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)
              : z.string().optional();
          break;

        case "date range":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z
                .object({
                  start: z.string().min(1, "Start date is required"),
                  end: z.string().min(1, "End date is required"),
                })
                .refine((data) => data.start <= data.end, {
                  message: "End date must be after start date",
                })
              : z
                .object({
                  start: z.string(),
                  end: z.string(),
                })
                .optional();
          break;

        case "time":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)
              : z.string().optional();
          break;

        case "time-range":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.object({
                start: z.string().min(1, "Start time is required"),
                end: z.string().min(1, "End time is required"),
              })
              : z
                .object({
                  start: z.string(),
                  end: z.string(),
                })
                .optional();
          break;

        case "phone":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string(z.string().min(1, "phone is requ"))
              : z.string().optional();
          break;
        case "multiple-option":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.array(z.string()).min(1, `${field.label_name} is required`)
              : z.array(z.string()).optional();
          break;

        default:
          schemaFields[fieldId] = z.any().optional();
      }
    });

    return z.object(schemaFields);
  };

  // Memoize the combined schema to avoid unnecessary recalculations
  const finalSchema = useMemo(() => {
    if (dynamicFields.length === 0) {
      return companySchema;
    }

    // Combine grouped and ungrouped fields
    const allDynamicFields = [
      ...(grouped?.flatMap((g) => g.groupFields || []) || []),
      ...(ungrouped || []),
    ];

    if (allDynamicFields.length === 0) {
      return companySchema;
    }

    const dynamicSchema = generateDynamicSchema(allDynamicFields);

    // Merge static and dynamic schemas
    return companySchema.merge(dynamicSchema);
  }, [grouped, ungrouped, dynamicFields]);

  // Form handling
  const {
    handleSubmit,
    control,
    register,
    clearErrors,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors, isValid },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(finalSchema),
    defaultValues: {
      rating: "3",
      business_type: "",
      countryId: "99",
    },
  });

  const companyName = watch("company_name");
  const debounceCompany = useDebounce(companyName || "", 300);

  // Field change handlers
  const handleEmailChange = (field, value, index) => {
    const newEmails = [...emails];
    newEmails[index] = { ...newEmails[index], [field]: value };
    setEmails(newEmails);
    validateEmails(newEmails);
  };

  const handleContactChange = (field, value, index, details = {}) => {
    const newContacts = [...contacts];
    newContacts[index] = {
      ...newContacts[index],
      [field]: value || "",
      ...details,
    };
    setContacts(newContacts);

    if (field === "phone" && value && value.trim() !== "") {
      setEmptyContactIndices((prev) => prev.filter((i) => i !== index));
    }
  };

  const validatePhoneNumber = (rawNumber, countryCode) => {
    try {
      const phoneNumber = parsePhoneNumber(
        String(rawNumber),
        String(countryCode?.toUpperCase())
      );
      const valid = phoneNumber?.isValid() || false;
      return valid;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setSearch(inputSearch.trim());
      setOffset(0);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [inputSearch]);

  useEffect(() => {
    validateContacts(contacts);
  }, [contacts]);

  useEffect(() => {
    let aborted = false;

    const checkEmail = async () => {
      const val = debouncedEmail?.trim();
      if (val) {
        setDuplicateEmailMessage((prev) => ({ ...prev, 0: null }));
        try {
          const isEmailExisting = await checkDuplicate({
            col: "email",
            customer_id: customer.customer_details?.customer_id,
            validating_val: val,
            contact_id: "",
            checkFor: "company",
            existing_val: emails[0]?.email || "",
          });
          if (!aborted) {
            setDuplicateEmailMessage((prev) => ({
              ...prev,
              0: isEmailExisting ? "Email already exists!" : null,
            }));
          }
        } catch (err) {
          if (!aborted) {
            setDuplicateEmailMessage((prev) => ({ ...prev, 0: null }));
          }
        }
      } else {
        setDuplicateEmailMessage((prev) => ({ ...prev, 0: null }));
      }
    };

    checkEmail();
    return () => {
      aborted = true;
    };
  }, [debouncedEmail, customer?.customer_details?.customer_id, emails]);

  useEffect(() => {
    let aborted = false;

    const checkPhone = async () => {
      const val = (debouncedPhone || "").trim();
      if (val) {
        setDuplicatePhoneMessage((prev) => ({ ...prev, 0: null }));
        try {
          const isPhoneExisting = await checkDuplicate({
            col: "phone",
            customer_id: customer.customer_details?.customer_id,
            validating_val: val,
            contact_id: "",
            checkFor: "company",
            existing_val: contacts[0]?.phone || "",
          });
          if (!aborted) {
            setDuplicatePhoneMessage((prev) => ({
              ...prev,
              0: isPhoneExisting ? "Phone number already exists!" : null,
            }));
          }
        } catch (err) {
          if (!aborted) {
            setDuplicatePhoneMessage((prev) => ({ ...prev, 0: null }));
          }
        }
      } else {
        setDuplicatePhoneMessage((prev) => ({ ...prev, 0: null }));
      }
    };

    checkPhone();
    return () => {
      aborted = true;
    };
  }, [debouncedPhone, customer?.customer_details?.customer_id, contacts]);

  useEffect(() => {
    const timers = [];

    emails.forEach((emailObj, index) => {
      if (!emailObj?.email) {
        setDuplicateEmailMessage((prev) => ({ ...prev, [index]: null }));
        return;
      }
      const t = setTimeout(async () => {
        try {
          const isDuplicate = await checkDuplicate({
            col: "email",
            customer_id: customer.customer_details?.customer_id,
            validating_val: emailObj.email.trim(),
            contact_id: "",
            checkFor: "company",
          });
          setDuplicateEmailMessage((prev) => ({
            ...prev,
            [index]: isDuplicate ? "Email already exists!" : null,
          }));
        } catch {
          // on error, do not set a false positive
        }
      }, 500);
      timers.push(t);
    });

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [emails, customer?.customer_details?.customer_id]);

  useEffect(() => {
    const timers = [];

    contacts.forEach((contact, index) => {
      if (!contact?.phone) {
        setDuplicatePhoneMessage((prev) => ({ ...prev, [index]: null }));
        return;
      }
      const t = setTimeout(async () => {
        try {
          const isDuplicate = await checkDuplicate({
            col: "phone",
            customer_id: customer.customer_details?.customer_id,
            validating_val: contact.phone.trim(),
            contact_id: "",
            checkFor: "company",
          });
          setDuplicatePhoneMessage((prev) => ({
            ...prev,
            [index]: isDuplicate ? "Phone number already exists!" : null,
          }));
        } catch {
          // no-op on error
        }
      }, 500);
      timers.push(t);
    });

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [contacts, customer?.customer_details?.customer_id]);

  useEffect(() => {
    const checkCompany = async () => {
      if (!debounceCompany) {
        setDuplicateCompanyMessage(null);
        return;
      }

      try {
        const isDuplicate = await checkCompanyDuplicate({
          customer_id: customer.customer_details?.customer_id,
          validating_val: debounceCompany,
          contact_id: "",
          checkFor: "company",
        });

        setDuplicateCompanyMessage(
          isDuplicate ? "Company already exists!" : null
        );
      } catch (error) {
        console.error("Error checking company duplicate:", error);
        setDuplicateCompanyMessage(null);
      }
    };

    const timer = setTimeout(checkCompany, 500);
    return () => clearTimeout(timer);
  }, [debounceCompany]);

  // Fetch countries when component mounts
  useEffect(() => {
    (async () => {
      try {
        const data = await getCountries();

        if (!data) return;
        const countryOptions = data.map((country) => ({
          value: country.id,
          label: country.name,
        }));
        setCountries(countryOptions);
        const indiaOption = countryOptions.find(
          (c) => c.value === 99 || c.label === "India"
        );
        if (indiaOption) {
          setSelectedCountry(indiaOption);
          setValue("countryId", indiaOption.value.toString());
        }
      } catch (error) {
        toast.error("Error Fetching Data");
        return;
      }
    })();
  }, [setValue]);

  useEffect(() => {
    (async () => {
      try {
        const countryId = selectedCountry ? selectedCountry.value : 99;
        const data = await getStates(countryId);
        if (!data || data === undefined) {
          setStates([]);
          setSelectedState(null);
          setValue("stateId", "");
          return;
        }
        const stateOptions = data.map((state) => ({
          value: state.id,
          label: state.state_name,
        }));
        setStates(stateOptions);
        setSelectedState(null);
        setValue("stateId", "");
      } catch (error) {
        toast.error("Error Fetching State");
        return;
      }
    })();
  }, [selectedCountry, setValue]);

  useEffect(() => {
    (async () => {
      try {
        if (!selectedState || selectedState === "") {
          setCity([]);
          setSelectedCity(null);
          setValue("cityId", "");
          return;
        }
        const data = await getCities(selectedState.value);
        if (!data || data === undefined) {
          setCity([]);
          return;
        }
        const cityOptions = data.map((city) => ({
          value: city.id,
          label: city.city_name,
        }));
        setCity(cityOptions);
        setSelectedCity(null);
        setValue("cityId", "");
      } catch (error) {
        toast.error("Error Fetching Cities");
        return;
      }
    })();
  }, [selectedState, setValue]);

  const fetchOwners = async () => {
    const res = await GetRequest(
      `users/users/fetchUserAsOwner/${customer.customer_details?.customer_id}`,
      { headers },
      { customer, setCustomer, rf: headers.Authentication, navigate }
    );

    if (res.err) {
      toast.error("Error Fetching Data");
      return;
    }

    const owners = res.data.map((lead) => ({
      leadName: lead.full_name,
      leadId: lead.id,
    }));

    setLeadOwner(owners);

    const currentOwner = getValues("leadOwnerId");
    if (!currentOwner && owners.length > 0) {
      setValue("leadOwnerId", owners[0].leadId, { shouldValidate: true });
    }
  };

  useEffect(() => {
    fetchOwners();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await GetRequest(
          `company/fetchCustomFields/${customer.customer_details?.customer_id}`,
          { headers },
          { customer, setCustomer, rf: headers.Authentication, navigate }
        );
        if (res.success === false || res.success === "fail") {
          toast.error(res.message);
          return;
        }
        console.log(res?.data, "saurabh singh");
        res.data?.grouped != {} &&
          setGrouped(res.data?.grouped && res.data?.grouped);
        res.data?.ungrouped != {} &&
          setUnGrouped(res.data?.ungrouped && res.data?.ungrouped);
        const allFields = [
          ...(res.data?.grouped?.flatMap((g) => g.groupFields || []) || []),
          ...(res.data?.ungrouped || []),
        ];
        setDynamicFields(allFields);
      } catch (error) {
        toast.error("Internal Server Error");
        return;
      }
    })();
  }, [customer]);

  // Helper function to scroll to form sections
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    const formContainer = document.querySelector(".form-container");

    if (element && formContainer) {
      formContainer.scrollTo({
        top: element.offsetTop - 110,
        behavior: "smooth",
      });
      setActiveSection(id);
    }
  };

  // Email field handlers
  const handleAddEmail = () => {
    setEmails([...emails, { email: "", email_type: "Office" }]);
  };
  const handleDeleteEmail = (index) => {
    const newEmails = emails.filter((_, i) => i !== index);
    setEmails(newEmails);
    validateEmails(newEmails);
  };

  // Validation functions
  const validateEmails = (emailList) => {
    const duplicates = emailList.reduce((acc, currentEmail, currentIndex) => {
      const isDuplicate = emailList.some(
        (email, otherIndex) =>
          currentIndex !== otherIndex &&
          currentEmail.email &&
          email.email &&
          currentEmail.email.toLowerCase() === email.email.toLowerCase()
      );
      if (isDuplicate) acc.push(currentIndex);
      return acc;
    }, []);
    setEmailErrors(duplicates);

    const validationErrors = emailList.reduce(
      (acc, currentEmail, currentIndex) => {
        if (currentEmail.email && !isValidEmail(currentEmail.email)) {
          acc.push(currentIndex);
        }
        return acc;
      },
      []
    );
    setEmailValidationErrors(validationErrors);
  };

  // Contact field handlers
  const handleAddContact = () => {
    setContacts([
      ...contacts,
      { phone: "", phone_type: "Mobile", country_code: "+91" },
    ]);
  };
  const handleDeleteContact = (index) => {
    const newContacts = contacts.filter((_, i) => i !== index);
    setContacts(newContacts);
    validateContacts(newContacts);
  };
  const validateContacts = (contactList) => {
    const duplicates = contactList.reduce(
      (acc, currentContact, currentIndex) => {
        const isDuplicate = contactList.some(
          (contact, otherIndex) =>
            currentIndex !== otherIndex &&
            currentContact.phone &&
            contact.phone &&
            currentContact.country_code &&
            contact.country_code &&
            `${currentContact.country_code}${currentContact.phone.replace(
              /\D/g,
              ""
            )}` === `${contact.country_code}${contact.phone.replace(/\D/g, "")}`
        );
        if (isDuplicate) acc.push(currentIndex);
        return acc;
      },
      []
    );
    setContactErrors(duplicates);

    const validationErrors = contactList.reduce(
      (acc, currentContact, currentIndex) => {
        if (
          currentContact.phone &&
          !isValidMobileNumber(currentContact.phone)
        ) {
          acc.push(currentIndex);
        }
        return acc;
      },
      []
    );
    setContactValidationErrors(validationErrors);
  };

  // React-select handlers
  const handleCountryChange = (selectedOption) => {
    setSelectedCountry(selectedOption);
    setValue(
      "countryId",
      selectedOption ? selectedOption.value.toString() : ""
    );
    setSelectedState(null);
    setSelectedCity(null);
    setValue("stateId", "");
    setValue("cityId", "");
  };
  const handleStateChange = (selectedOption) => {
    setSelectedState(selectedOption);
    setValue("stateId", selectedOption ? selectedOption.value.toString() : "");
    setSelectedCity(null);
    setValue("cityId", "");
  };
  const handleCityChange = (selectedOption) => {
    setSelectedCity(selectedOption);
    setValue("cityId", selectedOption ? selectedOption.value.toString() : "");
  };

  // Form submission handler
  const { mutate } = usePostRequest(
    "company/createCompany",
    true,
    { headers },
    { customer, setCustomer, rf: headers.Authentication, navigate }
  );

  const handleFormSubmit = async (formData) => {
    try {
      const isDuplicate = await checkCompanyDuplicate({
        customer_id: customer.customer_details?.customer_id,
        validating_val: formData.company_name,
        contact_id: "",
      });

      if (isDuplicate) {
        toast.error("Company with this name already exists!");
        return;
      }

      const newEmptyEmailIndices = emails.reduce((acc, email, index) => {
        if (!email.email || email.email.trim() === "") {
          acc.push(index);
        }
        return acc;
      }, []);
      const newEmptyContactIndices = contacts.reduce((acc, contact, index) => {
        if (!contact.phone || contact.phone.trim() === "") {
          acc.push(index);
        }
        return acc;
      }, []);
      setEmptyEmailIndices(newEmptyEmailIndices);
      setEmptyContactIndices(newEmptyContactIndices);

      if (
        newEmptyEmailIndices.length > 0 ||
        newEmptyContactIndices.length > 0
      ) {
        if (newEmptyEmailIndices.length > 0) {
          emailSectionRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          toast.error("Please fill all email fields");
        } else if (newEmptyContactIndices.length > 0) {
          contactSectionRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          toast.error("Please fill all contact fields");
        }
        return;
      }

      const hasInvalidEmailFormat = emails.some(
        (email) => email.email.trim() !== "" && !validator.isEmail(email.email)
      );
      if (hasInvalidEmailFormat) {
        emailSectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        toast.error("Please enter valid email addresses");
        return;
      }

      const custom_fields = [];
      const staticFieldsPayload = { ...formData };
      dynamicFields.forEach((field) => {
        const fieldName = field.id;
        if (fieldName && formData.hasOwnProperty(fieldName)) {
          let formattedValue = formData[fieldName];
          let isRangeField = false;

          if (
            field.label_type === "date range" ||
            field.label_type === "time-range"
          ) {
            isRangeField = true;
            if (
              !formattedValue ||
              (!formattedValue.start && !formattedValue.end)
            ) {
              formattedValue = { start: "", end: "" };
            }
          } else if (field.label_type === "multiple-option") {
            if (!Array.isArray(formattedValue)) {
              formattedValue = [];
            }
          }
          const customFieldEntry = {
            field_id: field.id.toString(),
            value: formattedValue,
            range: isRangeField ? true : false,
          };
          custom_fields.push(customFieldEntry);
          delete staticFieldsPayload[fieldName];
        }
      });

      const payload = {
        customer_id: customer_id,
        company_name: formData.company_name,
        website: formData?.website || "",
        industry: formData?.industry || "",
        business_type: formData?.business_type || "PRIVATE",
        owner_id: Number(formData?.leadOwnerId) || 1,
        email: emails[0]?.email || "",
        phone: contacts[0]?.phone.trim(),
        user_email: emails,
        user_phone: contacts,
        alternate_email: [],
        alternate_phone: [],
        timezone: selectedTimezone,
        address: formData.address || "",
        country: formData.countryId || "99",
        state: formData.stateId || "",
        city: formData.cityId || "",
        zipcode: formData.zipcode || "",
        about: formData.about || "",
        facebook: formData.facebook || "",
        twitter: formData.twitter || "",
        linkedin: formData.linkedin || "",
        instagram: formData.instagram || "",
        rating: formData.rating || "4",
        employee_size: formData.employee_size || '',
        user_login_id: customer.customer_details?.id,
      };

      const finalPayload = {
        ...payload,
        custom_fields,
      };

      mutate(finalPayload, {
        onSuccess: (data) => {
          if (data.status === "error") {
            if (typeof data.message === "object") {
              Object.values(data.message).forEach((msg) => {
                toast.error(msg);
              });
            } else {
              toast.error(data.message || "Something went wrong");
            }
            return;
          }
          if (data.status === "fail") {
            toast.error("Something went wrong");
            return;
          }

          if (data.status === "Expire") {
            UpdateRF(headers.Authentication);
            navigate("/login");
            return;
          }
          setRecentCompanyId &&
            setRecentCompanyId((prev) => ({
              ...prev,
              recentCompanyId: String(data.data),
            }));
          reset();
          setEmails([{ email_type: "Office", email: "" }]);
          setContacts([
            { country_code: "+91", phone_type: "Mobile", phone: "" },
          ]);
          setEmailErrors([]);
          setContactErrors([]);
          setEmailValidationErrors([]);
          setContactValidationErrors([]);
          setSelectedTimezone({
            value: "Asia/Kolkata",
            label: "(GMT+5:30) Chennai, Kolkata, Mumbai, New Delhi",
            offset: 5.5,
            abbrev: "IST",
            altName: "India Standard Time",
          });
          setSelectedCountry({ value: 99, label: "India" });
          setSelectedState(null);
          setSelectedCity(null);
          setValue("countryId", "99");
          setReloadKey && setReloadKey((prev) => prev + 1);
          setShow(false);
          setContactShow && setContactShow();
          toast.success(data.message || "Company created successfully");
          setHighlightId(String(data.data))
        },
        onError: (error) => {
          toast.error("Error creating company");
        },
      });
    } catch (error) {
      toast.error("Error creating company");
      console.error("Submission error:", error);
    }
  };

  const handleClose = () => {
    reset();
    setEmails([{ email_type: "Office", email: "" }]);
    setContacts([{ country_code: "+91", phone_type: "Mobile", phone: "" }]);
    setEmailErrors([]);
    setContactErrors([]);
    setEmailValidationErrors([]);
    fetchOwners();
    setContactValidationErrors([]);
    setSelectedTimezone({
      value: "Asia/Kolkata",
      label: "(GMT+5:30) Chennai, Kolkata, Mumbai, New Delhi",
      offset: 5.5,
      abbrev: "IST",
      altName: "India Standard Time",
    });
    setSelectedCountry({ value: 99, label: "India" });
    setSelectedState(null);
    setSelectedCity(null);
    setValue("countryId", "99");
    setContactShow && setContactShow(true)
    setShow(false);
  };


  return (
    <div
      className={`${contactsModule ? "" : "row headingCards mb-0 mt-4 w-100 ms-0"
        }`}
    >
      <div
        className={`${contactsModule ? "" : "d-flex justify-content-between px-3 w-100 py-2"
          }`}
      >
        <Offcanvas
          className="createContactOffcanvas"
          placement="end"
          backdrop="static"
          enforceFocus={true}
          show={show}
          onHide={handleClose}
        >
          <Offcanvas.Header
            closeButton
            className="border-bottom border-dashed pt-3"
          >
            <div className="d-flex align-items-center gap-2">
              <div className="pt-0 ps-4">
                <Offcanvas.Title
                  style={{ color: "#0791ff" }}
                  className="addEnquiryHead"
                >
                  Create Company
                </Offcanvas.Title>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#b26792",
                    lineHeight: 1.1,
                  }}
                  className="text-secondary text-nowrap mb-0"
                >
                  Create New Company For Future
                </p>
              </div>
            </div>
            <div
              className="d-flex justify-content-end align-items-center"
              style={{ width: "70%", border: "none" }}
            >
              <div type="button" className="form-check form-switch">
                <input
                  className="form-check-input"
                  checked={showOnlyRequired}
                  onChange={() => setShowOnlyRequired(prev => !prev)}
                  type="checkbox"
                  role="switch"
                  id="switchCheckChecked"
                />
                <span
                  className="form-check-label text-secondary"
                  htmlFor="switchCheckChecked"
                  style={{
                    fontSize: "14px",
                  }}
                >
                  Show & Hide Important Field
                </span>
              </div>
            </div>
            {/* TOGGLE BUTTON ADDED HERE */}
          </Offcanvas.Header>

          <Offcanvas.Body className="pt-0">
            <div style={{ display: "flex", height: "100%" }}>
              <Form
                className="form-container p-3 ps-0 w-100 pe-0"
                onSubmit={handleSubmit(handleFormSubmit)}
                style={{
                  margin: 0,
                  overflowY: "auto",
                  height: "100%",
                  paddingLeft: "20px",
                  position: "relative",
                }}
                encType="multipart/form-data"
              >
                {/* Basic Information Section */}
                <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                  <h4 id="BasicInformation" className="offcanvasSecHeading">
                    Basic Information
                  </h4>
                  <Row className="ms-0 w-100 pt-3">
                    {/* Company Name - Always show as required */}
                    <Col md={6}>
                      <Form.Group
                        className="mb-3 focus-z-index position-relative"
                        controlId="formCompanyName"
                      >
                        <Form.Label style={{ marginBottom: 1 }}>
                          Company Name <span className="text-danger">*</span>
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            {...register("company_name", {
                              required: "Company Name is required",
                              validate: async (value) => {
                                if (!value) return true;
                                const isDuplicate = await checkCompanyDuplicate(
                                  {
                                    customer_id:
                                      customer.customer_details?.customer_id,
                                    validating_val: value,
                                    contact_id: "",
                                  }
                                );
                                return (
                                  !isDuplicate || "Company already exists!"
                                );
                              },
                            })}
                            placeholder="Enter Company Name"
                          />
                        </InputGroup>

                        {errors.company_name && (
                          <p
                            className="text-danger mt-1"
                            style={{ fontSize: "12px" }}
                          >
                            {errors.company_name.message}
                          </p>
                        )}
                        {duplicateCompanyMessage && !errors.company_name && (
                          <p
                            className="text-danger mt-1"
                            style={{ fontSize: "12px" }}
                          >
                            {duplicateCompanyMessage}
                          </p>
                        )}
                      </Form.Group>
                    </Col>

                    {/* Website - Always show as required */}
                    {!showOnlyRequired && (
                      <Col md={6}>
                        <Form.Group
                          className="mb-3 focus-z-index position-relative"
                          controlId="formWebsite"
                        >
                          <Form.Label style={{ marginBottom: 1 }}>
                            Website
                          </Form.Label>

                          <InputGroup>
                            <Form.Control
                              type="text"
                              {...register("website")}
                              placeholder="Enter URL"
                            />
                          </InputGroup>


                        </Form.Group>
                      </Col>
                    )}

                    {/* Industry - Always show as required */}
                    <Col md={6}>
                      <Form.Group
                        className="mb-3 focus-z-index position-relative"
                        controlId="formIndustry"
                      >
                        <Form.Label style={{ marginBottom: 1 }}>
                          Industry <span className="text-danger">*</span>
                        </Form.Label>

                        <Controller
                          name="industry"
                          control={control}
                          rules={{ required: "Industry is required" }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              styles={customStyles}
                              className="basic-single w-100"
                              options={industryOptions}
                              value={
                                industryOptions.find(
                                  (option) => option.value === field.value
                                ) || null
                              }
                              onChange={(selectedOption) =>
                                field.onChange(selectedOption?.value || "")
                              }
                              classNamePrefix="react-select"
                              isSearchable
                              isClearable
                              placeholder="Select Industry"
                              noOptionsMessage={() => "No industries available"}
                            />
                          )}
                        />

                        {errors.industry && (
                          <p
                            className="text-danger"
                            style={{ fontSize: "12px" }}
                          >
                            industry is required
                          </p>
                        )}
                      </Form.Group>
                    </Col>

                    {/* Business Type - Always show as required */}
                    {!showOnlyRequired && (
                      <>
                        <Col md={6}>
                          <Form.Group
                            className="mb-3 focus-z-index position-relative"
                            controlId="formBusinessType"
                          >
                            <Form.Label style={{ marginBottom: 1 }}>
                              Business Type
                            </Form.Label>

                            <Controller
                              name="business_type"
                              control={control}
                              rules={{ required: "Business type is required" }}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  styles={customStyles}
                                  options={[
                                    {
                                      value: "PRIVATE",
                                      label: "Private Limited Company",
                                    },
                                    {
                                      value: "PUBLIC",
                                      label: "Public Limited Company",
                                    },
                                    {
                                      value: "LLP",
                                      label: "Limited Liability Partnership",
                                    },
                                    {
                                      value: "PROPRIETORSHIP",
                                      label: "Proprietorship",
                                    },
                                  ]}
                                  placeholder="Select"
                                  classNamePrefix="react-select"
                                  isClearable
                                  isSearchable
                                  value={
                                    field.value
                                      ? {
                                        value: field.value,
                                        label: {
                                          PRIVATE: "Private Limited Company",
                                          PUBLIC: "Public Limited Company",
                                          LLP: "Limited Liability Partnership",
                                          PROPRIETORSHIP: "Proprietorship",
                                        }[field.value],
                                      }
                                      : null
                                  }
                                  onChange={(option) =>
                                    field.onChange(option ? option.value : "")
                                  }
                                />
                              )}
                            />
                            {errors?.business_type && (
                              <p
                                className="text-danger"
                                style={{ fontSize: "12px" }}
                              >
                                {errors?.business_type.message}
                              </p>
                            )}
                          </Form.Group>
                        </Col>
                      </>
                    )}
                  </Row>

                  {/* Employee Size - Always show as required */}
                  <Row className="w-100 ms-0">
                    {!showOnlyRequired && (
                      <>
                        <Col md={6}>
                          <Form.Group
                            className="mb-3 focus-z-index position-relative"
                            controlId="formEmployeeSize"
                          >
                            <Form.Label style={{ marginBottom: 1 }}>
                              Employee Size
                            </Form.Label>

                            <Controller
                              name="employee_size"
                              control={control}
                              rules={{ required: "Employee size is required" }}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  styles={customStyles}
                                  className="basic-single w-100"
                                  classNamePrefix="react-select"
                                  options={employeeSizeOptions}
                                  value={
                                    employeeSizeOptions.find(
                                      (option) => option.value === field.value
                                    ) || null
                                  }
                                  onChange={(selectedOption) =>
                                    field.onChange(selectedOption?.value || "")
                                  }
                                  isSearchable
                                  isClearable
                                  placeholder="Select Employee Size"
                                  noOptionsMessage={() => "No sizes available"}
                                />
                              )}
                            />

                            {errors?.employee_size && (
                              <p
                                style={{ fontSize: "12px" }}
                                className="text-danger mt-1"
                              >
                                {"Employee size is required"}
                              </p>
                            )}
                          </Form.Group>
                        </Col>{" "}
                      </>
                    )}

                    {/* Lead Owner - Always show as required */}
                    <Col md={6}>
                      <Form.Group
                        className="mb-3 focus-z-index position-relative"
                        controlId="formOwner"
                      >
                        <Form.Label style={{ marginBottom: 1 }}>
                          Company Account Owner{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>

                        <Controller
                          name="leadOwnerId"
                          control={control}
                          rules={{ required: "Owner is required" }}
                          render={({ field }) => {
                            const options = leadOwner.map((lead) => ({
                              value: lead.leadId,
                              label: lead.leadName,
                            }));

                            return (
                              <Select
                                {...field}
                                options={options}
                                placeholder="Select Owner"
                                classNamePrefix="react-select"
                                styles={customStyles}
                                value={
                                  options.find(
                                    (opt) => opt.value === field.value
                                  ) || null
                                }
                                onChange={(selected) =>
                                  field.onChange(selected?.value || "")
                                }
                              />
                            );
                          }}
                        />

                        {errors.leadOwnerId && (
                          <p
                            style={{ fontSize: "12px" }}
                            className="text-danger mt-1"
                          >
                            {errors.leadOwnerId.message}
                          </p>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
                <hr />

                {/* Communication Section - Always show as contains required fields */}
                <div
                  style={{ paddingLeft: 0, paddingTop: 10 }}
                  ref={emailSectionRef}
                >
                  <h4 id="BasicInformation" className="offcanvasSecHeading">
                    Communication
                  </h4>
                  <div className="pt-3 d-flex flex-column w-100 ms-0">
                    <Row className="w-100 ms-0">
                      {/* Primary Email - Always show */}
                      <Col md={6}>
                        <Form.Group
                          className="mb-3 focus-z-index position-relative"
                          controlId="formEmail"
                        >
                          {emails.map((email, index) => {
                            const isDuplicate =
                              emails.filter(
                                (e) => e.email === email.email && e.email !== ""
                              ).length > 1;

                            return (
                              <div key={index}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 5,
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "200px",
                                      position: "relative",
                                    }}
                                    className="focus-z-index"
                                  >
                                    <Form.Label style={{ marginBottom: 1 }}>
                                      Email
                                      <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Select
                                      classNamePrefix="react-select"
                                      options={[
                                        { value: "Office", label: "Office" },
                                        {
                                          value: "Personal",
                                          label: "Personal",
                                        },
                                      ]}
                                      value={{
                                        value: email.email_type || "Office",
                                        label: email.email_type || "Office",
                                      }}
                                      onChange={(selectedOption) =>
                                        handleEmailChange(
                                          "email_type",
                                          selectedOption.value,
                                          index
                                        )
                                      }
                                      styles={{
                                        ...customStyles,
                                        control: (base, state) => ({
                                          ...base,
                                          ...((customStyles?.control &&
                                            customStyles.control(
                                              base,
                                              state
                                            )) ||
                                            {}),
                                          minHeight: "41px",
                                          fontSize: "14px",
                                        }),
                                        valueContainer: (base) => ({
                                          ...base,
                                          ...((customStyles?.valueContainer &&
                                            customStyles.valueContainer(
                                              base
                                            )) ||
                                            {}),
                                          padding: "2px 8px",
                                        }),
                                      }}
                                    />
                                  </div>

                                  <InputGroup>
                                    <Form.Label style={{ marginBottom: 1 }}>
                                      Email Address
                                      <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                      style={{
                                        height: "40px",
                                        border: "1px solid #c5c5c5",
                                      }}
                                      type="email"
                                      value={email.email}
                                      onChange={(e) =>
                                        handleEmailChange(
                                          "email",
                                          e.target.value,
                                          index
                                        )
                                      }
                                      required={index === 0}
                                      placeholder="ex: abc@gmail.com"
                                      className={
                                        emailErrors.includes(index) ||
                                          emailValidationErrors.includes(index) ||
                                          isDuplicate ||
                                          emptyEmailIndices.includes(index)
                                          ? "border-danger"
                                          : ""
                                      }
                                    />
                                  </InputGroup>

                                  {emails.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteEmail(index)}
                                      style={{
                                        width: "24px",
                                        height: "24px",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        border: "0.4px solid red",
                                        borderRadius: "50px",
                                        cursor: "pointer",
                                        marginLeft: "5px",
                                      }}
                                    >
                                      <IconTrash
                                        stroke={2}
                                        style={{ stroke: "red" }}
                                        className="w-3 h-3"
                                      />
                                    </button>
                                  )}
                                </div>

                                {isDuplicate && (
                                  <p
                                    className="text-danger"
                                    style={{
                                      fontSize: "12px",
                                      marginTop: "2px",
                                    }}
                                  >
                                    Duplicate email
                                  </p>
                                )}
                                {emailValidationErrors.includes(index) && (
                                  <p
                                    className="text-danger"
                                    style={{
                                      fontSize: "12px",
                                      marginTop: "2px",
                                    }}
                                  >
                                    Invalid email format
                                  </p>
                                )}
                                {emptyEmailIndices.includes(index) && (
                                  <p
                                    className="text-danger"
                                    style={{
                                      fontSize: "12px",
                                      marginTop: "2px",
                                    }}
                                  >
                                    Email is required
                                  </p>
                                )}
                                {duplicateEmailMessage?.[index] && (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      fontSize: "12px",
                                      gap: 2,
                                      paddingTop: "4px",
                                    }}
                                    className="text-danger"
                                  >
                                    <IconCircleX
                                      stroke={2}
                                      style={{ width: "14px", height: "14px" }}
                                    />
                                    {duplicateEmailMessage[index]}
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          <button
                            type="button"
                            onClick={handleAddEmail}
                            style={{
                              fontSize: "14px",
                              border: 0,
                              color: "#777",
                              backgroundColor: "transparent",
                              marginBottom: "13px",
                            }}
                          >
                            Add Email{" "}
                            <IconPlus
                              stroke={2}
                              style={{
                                width: "15px",
                                height: "15px",
                                border: "none",
                              }}
                            />
                          </button>
                        </Form.Group>
                      </Col>

                      {/* Primary Contact - Always show */}
                      <Col md={6} ref={contactSectionRef}>
                        <Form.Group
                          className="mb-3 focus-z-index position-relative"
                          controlId="formContact"
                        >
                          {contacts.map((contact, index) => {
                            const isDuplicate =
                              contacts.filter(
                                (c) =>
                                  c.phone === contact.phone &&
                                  c.country_code === contact.country_code &&
                                  contact.phone !== ""
                              ).length > 1;

                            return (
                              <div key={index}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 5,
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "200px",
                                      position: "relative",
                                    }}
                                    className="focus-z-index"
                                  >
                                    <Form.Label style={{ marginBottom: 1 }}>
                                      Contact{" "}
                                      <span className="text-danger">*</span>
                                    </Form.Label>

                                    <Select
                                      classNamePrefix="react-select"
                                      options={[
                                        { value: "Mobile", label: "Mobile" },
                                        { value: "Work", label: "Work" },
                                        { value: "Home", label: "Home" },
                                        {
                                          value: "Personal",
                                          label: "Personal",
                                        },
                                      ]}
                                      value={{
                                        value: contact.phone_type || "Mobile",
                                        label: contact.phone_type || "Mobile",
                                      }}
                                      onChange={(selectedOption) =>
                                        handleContactChange(
                                          "phone_type",
                                          selectedOption.value || "Mobile",
                                          index
                                        )
                                      }
                                      styles={{
                                        ...customStyles,
                                        control: (base, state) => ({
                                          ...base,
                                          ...((customStyles?.control &&
                                            customStyles.control(
                                              base,
                                              state
                                            )) ||
                                            {}),
                                          minHeight: "41px",
                                          fontSize: "14px",
                                        }),
                                        valueContainer: (base) => ({
                                          ...base,
                                          ...((customStyles?.valueContainer &&
                                            customStyles.valueContainer(
                                              base
                                            )) ||
                                            {}),
                                          padding: "2px 8px",
                                        }),
                                      }}
                                    />
                                  </div>

                                  <InputGroup>
                                    <Form.Label>
                                      Mobile Number{" "}
                                      <span className="text-danger">*</span>
                                    </Form.Label>
                                    <PhoneInput
                                      country={"in"}
                                      countryCodeEditable={false}
                                      buttonStyle={{ border: "none" }}
                                      inputStyle={{
                                        width: "320px",
                                        height: "40px",
                                        border: "none",
                                      }}
                                      containerStyle={{
                                        border: `1px solid ${contactErrors.includes(index) ||
                                          contactValidationErrors.includes(
                                            index
                                          ) ||
                                          isDuplicate ||
                                          emptyContactIndices.includes(index)
                                          ? "#dc3545"
                                          : "#c5c5c5"
                                          }`,
                                        borderRadius: "5px",
                                      }}
                                      value={`${contact.country_code}${contact.phone}`}
                                      onChange={(
                                        value,
                                        country,
                                        e,
                                        rawInputValue
                                      ) => {
                                        const countryCode = country.dialCode;
                                        const rawNum =
                                          rawInputValue?.replace(/\D/g, "") ||
                                          "";
                                        handleContactChange(
                                          "phone",
                                          rawNum.slice(countryCode.length),
                                          index,
                                          {
                                            country_code: `+${countryCode}`,
                                          }
                                        );
                                      }}
                                      inputProps={{
                                        required: index === 0,
                                        name: "contact",
                                      }}
                                    />
                                  </InputGroup>

                                  {contacts.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteContact(index)}
                                      style={{
                                        width: "24px",
                                        height: "24px",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        border: "0.4px solid red",
                                        borderRadius: "50px",
                                        cursor: "pointer",
                                        marginLeft: "5px",
                                      }}
                                    >
                                      <IconTrash
                                        stroke={2}
                                        style={{
                                          width: "12px",
                                          height: "12px",
                                          stroke: "red",
                                        }}
                                      />
                                    </button>
                                  )}
                                </div>

                                {isDuplicate && (
                                  <p
                                    className="text-danger"
                                    style={{
                                      fontSize: "12px",
                                      marginTop: "2px",
                                    }}
                                  >
                                    Duplicate contact
                                  </p>
                                )}
                                {contactValidationErrors.includes(index) && (
                                  <p
                                    className="text-danger"
                                    style={{
                                      fontSize: "12px",
                                      marginTop: "2px",
                                    }}
                                  >
                                    Invalid phone number
                                  </p>
                                )}
                                {emptyContactIndices.includes(index) && (
                                  <p
                                    className="text-danger"
                                    style={{
                                      fontSize: "12px",
                                      marginTop: "2px",
                                    }}
                                  >
                                    Phone number is required
                                  </p>
                                )}
                                {duplicatePhoneMessage?.[index] && (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      fontSize: "12px",
                                      gap: 2,
                                      paddingTop: "4px",
                                    }}
                                    className="text-danger"
                                  >
                                    <IconCircleX
                                      stroke={2}
                                      style={{ width: "14px", height: "14px" }}
                                    />
                                    {duplicatePhoneMessage[index]}
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          <button
                            type="button"
                            onClick={handleAddContact}
                            style={{
                              fontSize: "14px",
                              border: 0,
                              color: "#777",
                              backgroundColor: "transparent",
                            }}
                          >
                            Add Contact{" "}
                            <IconPlus
                              stroke={2}
                              style={{
                                width: "15px",
                                height: "15px",
                                border: "none",
                              }}
                            />
                          </button>
                        </Form.Group>
                      </Col>

                      {/* Timezone Picker - Always show */}
                      <Col md={12}>
                        <Form.Group
                          className="mb-3 focus-z-index position-relative"
                          controlId="formTimezone"
                        >
                          <div className="d-flex flex-column gap-1">
                            <Form.Label
                              style={{ marginBottom: 1 }}
                              className="fw-lighter"
                            >
                              Select Timezone
                            </Form.Label>
                            <div style={{ fontSize: "14px" }}>
                              <TimezoneSelect
                                styles={customStyles}
                                timezones={{
                                  ...allTimezones,
                                  "America/Lima": "Pittsburgh",
                                  "Europe/Berlin": "Frankfurt",
                                }}
                                value={selectedTimezone}
                                onChange={setSelectedTimezone}
                                classNamePrefix="react-select"
                              />
                            </div>
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                </div>
                <hr />

                <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                  <h4
                    id="BasicInformation"
                    className="offcanvasSecHeading pb-3"
                  >
                    Location
                  </h4>
                  {!showOnlyRequired && (
                    <>
                      <Row className="ms-0  w-100">
                        <Col md={12}>
                          <Form.Group
                            className="mb-3 focus-z-index position-relative d-flex flex-column"
                            controlId="formAddress"
                          >
                            <Form.Label
                              style={{ marginBottom: "2px", marginTop: "2px" }}
                            >
                              Address
                            </Form.Label>
                            <InputGroup>
                              <Form.Control
                                as={"textarea"}
                                {...register("address")}
                                rows={3}
                              />
                            </InputGroup>
                          </Form.Group>
                        </Col>
                      </Row>
                    </>
                  )}


                  <Row className="ms-0 w-100">
                    {/* COUNTRY */}
                    <Col md={6}>
                      <Form.Group className="mb-3 focus-z-index position-relative d-flex flex-column">
                        <Form.Label className="fw-lighter">
                          Select Country <span className="text-danger">*</span>
                        </Form.Label>

                        <Controller
                          name="countryId"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              styles={customStyles}
                              options={country}
                              isClearable
                              classNamePrefix="react-select"
                              isSearchable
                              placeholder="Select Country"
                              value={
                                country.find((c) => c.value === field.value) ||
                                null
                              }
                              onChange={(option) => {
                                field.onChange(option ? option.value : "");
                                handleCountryChange(option);
                              }}
                            />
                          )}
                        />

                        {errors.countryId && (
                          <p className="text-danger mt-1">
                            {errors.countryId.message}
                          </p>
                        )}
                      </Form.Group>
                    </Col>

                    {/* STATE */}
                    <Col md={6}>
                      <Form.Group className="mb-3 focus-z-index position-relative d-flex flex-column">
                        <Form.Label className="fw-lighter">
                          Select State <span className="text-danger">*</span>
                        </Form.Label>

                        <Controller
                          name="stateId"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              styles={customStyles}
                              options={states}
                              classNamePrefix="react-select"
                              isClearable
                              isSearchable
                              isDisabled={!selectedCountry}
                              placeholder="Select State"
                              value={
                                states.find((s) => s.value === field.value) ||
                                null
                              }
                              onChange={(option) => {
                                field.onChange(option ? option.value : "");
                                handleStateChange(option);
                              }}
                            />
                          )}
                        />

                        {errors.stateId && (
                          <p className="text-danger mt-1">
                            {errors.stateId.message}
                          </p>
                        )}
                      </Form.Group>
                    </Col>

                    {/* CITY */}
                    <Col md={6}>
                      <Form.Group className="mb-3 focus-z-index position-relative d-flex flex-column">
                        <Form.Label className="fw-lighter">
                          Select City <span className="text-danger">*</span>
                        </Form.Label>

                        <Controller
                          name="cityId"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              styles={customStyles}
                              options={city}
                              isClearable
                              isSearchable
                              classNamePrefix="react-select"
                              placeholder="Select City"
                              value={
                                city.find((c) => c.value === field.value) ||
                                null
                              }
                              onChange={(option) => {
                                field.onChange(option ? option.value : "");
                                handleCityChange(option);
                              }}
                            />
                          )}
                        />

                        {errors.cityId && (
                          <p className="text-danger mt-1">
                            {errors.cityId.message}
                          </p>
                        )}
                      </Form.Group>
                    </Col>

                    {/* ZIPCODE */}
                    {!showOnlyRequired && (
                      <>
                        <Col md={6}>
                          <Form.Group className="mb-3 focus-z-index position-relative d-flex flex-column">
                            <Form.Label>Enter Zipcode</Form.Label>

                            <InputGroup>
                              <Form.Control
                                type="text"
                                {...register("zipcode")}
                                placeholder="Enter Zipcode"
                                onKeyPress={(e) => {
                                  if (!/[0-9]/.test(e.key)) e.preventDefault();
                                }}
                                onPaste={(e) => {
                                  const pasteData =
                                    e.clipboardData.getData("text");
                                  if (!/^\d+$/.test(pasteData))
                                    e.preventDefault();
                                }}
                              />
                            </InputGroup>

                            {errors.zipcode && (
                              <p className="text-danger mt-1">
                                {errors.zipcode.message}
                              </p>
                            )}
                          </Form.Group>
                        </Col>{" "}
                      </>
                    )}
                  </Row>
                </div>
                <hr />

                {/* Social Links Section - Conditionally show */}
                {!showOnlyRequired && (
                  <>
                    <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                      <h4 id="BasicInformation" className="offcanvasSecHeading">
                        Social
                      </h4>
                      <Row className="ms-0 pt-3 w-100">
                        <Col md={6}>
                          <Form.Group
                            className="mb-3 focus-z-index position-relative"
                            controlId="formFacebook"
                          >
                            <Form.Label style={{ marginBottom: 1 }}>
                              Facebook
                            </Form.Label>
                            <InputGroup>
                              <Form.Control
                                type="text"
                                {...register("facebook")}
                                placeholder="Enter URL "
                                isInvalid={!!errors.facebook}
                              />
                              {errors.facebook && (
                                <Form.Control.Feedback type="invalid">
                                  {errors.facebook.message}
                                </Form.Control.Feedback>
                              )}
                            </InputGroup>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group
                            className="mb-3 focus-z-index position-relative"
                            controlId="formLinkedin"
                          >
                            <Form.Label style={{ marginBottom: 1 }}>
                              LinkedIn
                            </Form.Label>
                            <InputGroup>
                              <Form.Control
                                type="text"
                                {...register("linkedin")}
                                placeholder="Enter URL"
                                isInvalid={!!errors.linkedin}
                              />
                              {errors.linkedin && (
                                <Form.Control.Feedback type="invalid">
                                  {errors.linkedin.message}
                                </Form.Control.Feedback>
                              )}
                            </InputGroup>
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row className="ms-0 w-100">
                        <Col md={6}>
                          <Form.Group
                            className="mb-3 focus-z-index position-relative"
                            controlId="formInstagram"
                          >
                            <Form.Label style={{ marginBottom: 1 }}>
                              Instagram
                            </Form.Label>
                            <InputGroup>
                              <Form.Control
                                type="text"
                                {...register("instagram")}
                                placeholder="Enter URL"
                                isInvalid={!!errors.instagram}
                              />
                              {errors.instagram && (
                                <Form.Control.Feedback type="invalid">
                                  {errors.instagram.message}
                                </Form.Control.Feedback>
                              )}
                            </InputGroup>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group
                            className="mb-3 focus-z-index position-relative"
                            controlId="formTwitter"
                          >
                            <Form.Label style={{ marginBottom: 1 }}>
                              Twitter
                            </Form.Label>
                            <InputGroup>
                              <Form.Control
                                type="text"
                                {...register("twitter")}
                                placeholder="Enter URL"
                                isInvalid={!!errors.twitter}
                              />
                              {errors.twitter && (
                                <Form.Control.Feedback type="invalid">
                                  {errors.twitter.message}
                                </Form.Control.Feedback>
                              )}
                            </InputGroup>
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                    <hr />
                  </>
                )}

                {/* About Section - Conditionally show */}
                {!showOnlyRequired && (
                  <>
                    <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                      <h4 id="BasicInformation" className="offcanvasSecHeading">
                        About
                      </h4>
                      <Row className="ms-0 pt-3 w-100">
                        <Col md={12}>
                          <Form.Group
                            className="mb-3 focus-z-index position-relative d-flex flex-column"
                            controlId="formAbout"
                          >
                            <Form.Label
                              style={{ marginBottom: "2px", marginTop: "2px" }}
                            >
                              About Company
                            </Form.Label>
                            <InputGroup>
                              <Form.Control
                                as={"textarea"}
                                {...register("about")}
                                placeholder="Tell us about your company"
                                rows={4}
                              />
                            </InputGroup>
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row className="ms-0 w-100">
                        <Col md={6}>
                          <Form.Group
                            className="mb-3 position-relative"
                            controlId="formRating"
                          >
                            <Form.Label style={{ marginBottom: 1 }}>
                              Rating <span className="text-danger">*</span>
                            </Form.Label>
                            <div className="d-flex align-items-center gap-1 pt-4 ps-2">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                  key={star}
                                  type="button"
                                  className="border-0 bg-transparent p-0"
                                  style={{ cursor: "pointer" }}
                                  onMouseEnter={() => setHoverRating(star)}
                                  onMouseLeave={() => setHoverRating(0)}
                                  onClick={() =>
                                    setValue("rating", star.toString())
                                  }
                                >
                                  {star <=
                                    (hoverRating || Number(watch("rating"))) ? (
                                    <IconStarFilled size={24} color="#ffc107" />
                                  ) : (
                                    <IconStar size={24} color="#dee2e6" />
                                  )}
                                </button>
                              ))}
                              <span className="ms-2 text-muted">
                                ({watch("rating")} star
                                {watch("rating") !== "1" ? "s" : ""})
                              </span>
                            </div>
                            <input type="hidden" {...register("rating")} />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  </>
                )}

                {/* grouped custom fields */}
                <div style={{ marginBottom: "100px" }}>
                  {grouped &&
                    grouped.length !== 0 &&
                    grouped.map((g) => {
                      const capitalizedName = g.group_name.replace(
                        /\b\w/g,
                        (char) => char.toUpperCase()
                      );

                      // Filter fields based on toggle
                      const filteredGroupFields =
                        g.groupFields?.filter(shouldShowField) || [];

                      // Only show the group if it has fields after filtering
                      if (
                        filteredGroupFields.length === 0 &&
                        showOnlyRequired
                      ) {
                        return null;
                      }

                      return (
                        <React.Fragment
                          key={g.id}
                        >
                          {filteredGroupFields.length !== 0 && <hr />}
                          <div>
                            <h4
                              id={capitalizedName}
                              className="offcanvasSecHeading"
                            >
                              {filteredGroupFields.length !== 0 &&
                                capitalizedName}
                            </h4>
                            <Row className="w-100 ms-0 pt-3">
                              {filteredGroupFields.length !== 0 &&
                                filteredGroupFields.map((field) => {
                                  return (
                                    <React.Fragment key={field.id}>
                                      {field.label_type === "text" && (
                                        <Col md={6}>
                                          <Text
                                            name={field.id}
                                            label_name={field.label_name}
                                            label_type={field.label_type}
                                            control={control}
                                            rules={{
                                              required: `${field.label_name} is required`,
                                            }}
                                            isRequired={
                                              field.is_required === "yes"
                                            }
                                            isImportant={
                                              field.is_important === "yes"
                                            }
                                          />
                                        </Col>
                                      )}
                                      {field.label_type === "monetary" && (
                                        <Col md={6}>
                                          <Text
                                            name={field.id}
                                            label_name={field.label_name}
                                            label_type={field.label_type}
                                            control={control}
                                            rules={{
                                              required: `${field.label_name} is required`,
                                            }}
                                            isRequired={
                                              field.is_required === "yes"
                                            }
                                            isImportant={
                                              field.is_important === "yes"
                                            }
                                          />
                                        </Col>
                                      )}
                                      {field.label_type === "largeText" && (
                                        <Col md={6}>
                                          <Text
                                            name={field.id}
                                            label_name={field.label_name}
                                            label_type={field.label_type}
                                            control={control}
                                            rules={{
                                              required: `${field.label_name} is required`,
                                            }}
                                            isRequired={
                                              field.is_required === "yes"
                                            }
                                            isImportant={
                                              field.is_important === "yes"
                                            }
                                          />
                                        </Col>
                                      )}
                                      {field.label_type === "numerical" && (
                                        <Col md={6}>
                                          <Text
                                            name={field.id}
                                            label_name={field.label_name}
                                            label_type={field.label_type}
                                            control={control}
                                            rules={{
                                              required: `${field.label_name} is required`,
                                            }}
                                            isRequired={
                                              field.is_required === "yes"
                                            }
                                            isImportant={
                                              field.is_important === "yes"
                                            }
                                          />
                                        </Col>
                                      )}
                                      {field.label_type === "address" && (
                                        <Col md={6}>
                                          <Text
                                            name={field.id}
                                            label_name={field.label_name}
                                            label_type={field.label_type}
                                            control={control}
                                            rules={{
                                              required: `${field.label_name} is required`,
                                            }}
                                            isRequired={
                                              field.is_required === "yes"
                                            }
                                            isImportant={
                                              field.is_important === "yes"
                                            }
                                          />
                                        </Col>
                                      )}
                                      {field.label_type === "single-option" && (
                                        <Col md={6}>
                                          <SelectOpt
                                            name={field.id}
                                            label_name={field.label_name}
                                            label_type={field.label_type}
                                            control={control}
                                            rules={{
                                              required: `${field.label_name} is required`,
                                            }}
                                            isRequired={
                                              field.is_required === "yes"
                                            }
                                            isImportant={
                                              field.is_important === "yes"
                                            }
                                            options={
                                              field.option !== null &&
                                              field.option
                                            }
                                          />
                                        </Col>
                                      )}
                                      {field.label_type === "user" && (
                                        <Col md={6}>
                                          <SelectOpt
                                            name={field.id}
                                            label_name={field.label_name}
                                            label_type="single-option"
                                            control={control}
                                            rules={{
                                              required: `${field.label_name} is required`,
                                            }}
                                            isRequired={
                                              field.is_required === "yes"
                                            }
                                            isImportant={
                                              field.is_important === "yes"
                                            }
                                            options={leadOwner.map((lead) => ({
                                              value: lead.leadId,
                                              label: lead.leadName,
                                            }))}
                                          />
                                        </Col>
                                      )}
                                      {field.label_type === "person" && (
                                        <Col md={6}>
                                          <SelectOpt
                                            name={field.id}
                                            label_name={field.label_name}
                                            label_type="single-option"
                                            control={control}
                                            rules={{
                                              required: `${field.label_name} is required`,
                                            }}
                                            isRequired={
                                              field.is_required === "yes"
                                            }
                                            isImportant={
                                              field.is_important === "yes"
                                            }
                                            options={
                                              allContacts.length > 0
                                                ? allContacts
                                                : []
                                            }
                                          />
                                        </Col>
                                      )}
                                      {field.label_type === "organization" && (
                                        <Col md={6}>
                                          <SelectOpt
                                            name={field.id}
                                            label_name={field.label_name}
                                            label_type="single-option"
                                            control={control}
                                            rules={{
                                              required: `${field.label_name} is required`,
                                            }}
                                            isRequired={
                                              field.is_required === "yes"
                                            }
                                            isImportant={
                                              field.is_important === "yes"
                                            }
                                          />
                                        </Col>
                                      )}
                                      {field.label_type === "time" && (
                                        <Col md={6}>
                                          <Time
                                            name={field.id}
                                            label_name={field.label_name}
                                            label_type={field.label_type}
                                            control={control}
                                            isRequired={
                                              field.is_required === "yes"
                                            }
                                            isImportant={
                                              field.is_important === "yes"
                                            }
                                          />
                                        </Col>
                                      )}
                                      {field.label_type === "time-range" && (
                                        <Col md={6}>
                                          <Time
                                            name={field.id}
                                            label_name={field.label_name}
                                            label_type={field.label_type}
                                            control={control}
                                            rules={{
                                              required:
                                                field.is_required === "yes" &&
                                                "Working hours are required",
                                            }}
                                            isRequired={
                                              field.is_required === "yes"
                                            }
                                            isImportant={
                                              field.is_important === "yes"
                                            }
                                          />
                                        </Col>
                                      )}

                                      {field.label_type === "phone" && (
                                        <Col md={6}>
                                          <PhoneNumberInput
                                            name={field.id}
                                            label_name={field.label_name}
                                            label_type={field.label_type}
                                            control={control}
                                            rules={{
                                              required:
                                                field.is_required === "yes" &&
                                                "Phone number is required",
                                            }}
                                            isRequired={
                                              field.is_required === "yes"
                                            }
                                            validatePhoneNumber={
                                              validatePhoneNumber
                                            }
                                            isImportant={
                                              field.is_important === "yes"
                                            }
                                          />
                                        </Col>
                                      )}
                                      {field.label_type === "date" && (
                                        <Col md={6}>
                                          <DateField
                                            name={field.id}
                                            label_name={field.label_name}
                                            label_type={field.label_type}
                                            control={control}
                                            rules={{
                                              required:
                                                field.is_required === "yes" &&
                                                "Date is required",
                                            }}
                                            isRequired={
                                              field.is_required === "yes"
                                            }
                                            isImportant={
                                              field.is_important === "yes"
                                            }
                                          />
                                        </Col>
                                      )}
                                      {field.label_type === "date range" && (
                                        <Col md={6}>
                                          <DateField
                                            name={field.id}
                                            label_name={field.label_name}
                                            label_type={field.label_type}
                                            control={control}
                                            // rules={{
                                            //   required:
                                            //     field.is_required === "yes" &&
                                            //     "Date Range number is required",
                                            //   validate: {
                                            //     validRange: (value) => {
                                            //       if (
                                            //         !value?.start ||
                                            //         !value?.end
                                            //       )
                                            //         return true;
                                            //       return (
                                            //         value.start <= value.end ||
                                            //         "End date must be after or same as start date"
                                            //       );
                                            //     },
                                            //   },
                                            // }}
                                            isImportant={
                                              field.is_important === "yes"
                                            }
                                            isRequired={
                                              field.is_required === "yes"
                                            }
                                          />
                                        </Col>
                                      )}
                                      {field.label_type ===
                                        "multiple-option" && (
                                          <Col md={6}>
                                            <MultipleOpt
                                              name={field.id}
                                              label_name={field.label_name}
                                              label_type={field.label_type}
                                              control={control}
                                              isRequired={
                                                field.is_required === "yes"
                                                  ? true
                                                  : false
                                              }
                                              isImportant={
                                                field.is_important === "yes"
                                                  ? true
                                                  : false
                                              }
                                              options={
                                                field.option &&
                                                JSON.parse(field.option)
                                              }
                                              rules={{
                                                required:
                                                  field.is_required === "yes" &&
                                                  "Teams is required",
                                              }}
                                            />
                                          </Col>
                                        )}
                                    </React.Fragment>
                                  );
                                })}
                            </Row>
                          </div>
                        </React.Fragment>
                      );
                    })}

                  {/* {ungrouped?.filter(shouldShowField).length !== 0 && <hr />} */}
                  <Row
                    className="w-100 ms-0 mt-3"
                    style={{ paddingLeft: "20px" }}
                  >
                    {ungrouped &&
                      ungrouped.length !== 0 &&
                      ungrouped.filter(shouldShowField).map((field) => {
                        return (
                          <React.Fragment key={field.id}>
                            {field.label_type === "text" && (
                              <Col md={6}>
                                <Text
                                  name={field.id}
                                  label_name={field.label_name}
                                  label_type={field.label_type}
                                  labelClass="mb-3 focus-z-index position-relative"
                                  control={control}
                                  rules={{
                                    required: `${field.label_name} is required`,
                                  }}
                                  isRequired={field.is_required === "yes"}
                                  isImportant={field.is_important === "yes"}
                                />
                              </Col>
                            )}
                            {field.label_type === "monetary" && (
                              <Col md={6}>
                                <Text
                                  name={field.id}
                                  label_name={field.label_name}
                                  label_type={field.label_type}
                                  labelClass="mb-3 focus-z-index position-relative"
                                  control={control}
                                  rules={{
                                    required: `${field.label_name} is required`,
                                  }}
                                  isRequired={field.is_required === "yes"}
                                  isImportant={field.is_important === "yes"}
                                />
                              </Col>
                            )}
                            {field.label_type === "largeText" && (
                              <Col md={6}>
                                <Text
                                  name={field.id}
                                  label_name={field.label_name}
                                  label_type={field.label_type}
                                  labelClass="mb-3 focus-z-index position-relative"
                                  control={control}
                                  rules={{
                                    required: `${field.label_name} is required`,
                                  }}
                                  isRequired={field.is_required === "yes"}
                                  isImportant={field.is_important === "yes"}
                                />
                              </Col>
                            )}
                            {field.label_type === "numerical" && (
                              <Col md={6}>
                                <Text
                                  name={field.id}
                                  label_name={field.label_name}
                                  label_type={field.label_type}
                                  labelClass="mb-3 focus-z-index position-relative"
                                  control={control}
                                  rules={{
                                    required: `${field.label_name} is required`,
                                  }}
                                  isRequired={field.is_required === "yes"}
                                  isImportant={field.is_important === "yes"}
                                />
                              </Col>
                            )}
                            {field.label_type === "address" && (
                              <Col md={6}>
                                <Text
                                  name={field.id}
                                  label_name={field.label_name}
                                  label_type={field.label_type}
                                  labelClass="mb-3 focus-z-index position-relative"
                                  control={control}
                                  rules={{
                                    required: `${field.label_name} is required`,
                                  }}
                                  isRequired={field.is_required === "yes"}
                                  isImportant={field.is_important === "yes"}
                                />
                              </Col>
                            )}
                            {field.label_type === "single-option" && (
                              <Col md={6}>
                                <SelectOpt
                                  name={field.id}
                                  label_name={field.label_name}
                                  label_type={field.label_type}
                                  labelClass="mb-3 focus-z-index position-relative"
                                  control={control}
                                  rules={{
                                    required: `${field.label_name} is required`,
                                  }}
                                  isRequired={field.is_required === "yes"}
                                  isImportant={field.is_important === "yes"}
                                  options={
                                    field.option !== null && field.option
                                  }
                                />
                              </Col>
                            )}
                            {field.label_type === "user" && (
                              <Col md={6}>
                                <SelectOpt
                                  name={field.id}
                                  label_name={field.label_name}
                                  label_type={field.label_type}
                                  control={control}
                                  labelClass="mb-3 focus-z-index position-relative"
                                  rules={{
                                    required: `${field.label_name} is required`,
                                  }}
                                  isRequired={field.is_required === "yes"}
                                  isImportant={field.is_important === "yes"}
                                  options={leadOwner}
                                />
                              </Col>
                            )}
                            {field.label_type === "person" && (
                              <Col md={6}>
                                <SelectOpt
                                  name={field.id}
                                  label_name={field.label_name}
                                  label_type={field.label_type}
                                  control={control}
                                  labelClass="mb-3 focus-z-index position-relative"
                                  rules={{
                                    required: `${field.label_name} is required`,
                                  }}
                                  isRequired={field.is_required === "yes"}
                                  isImportant={field.is_important === "yes"}
                                  options={
                                    allContacts.length > 0 ? allContacts : []
                                  }
                                />
                              </Col>
                            )}
                            {field.label_type === "organization" && (
                              <Col md={6}>
                                <SelectOpt
                                  name={field.id}
                                  label_name={field.label_name}
                                  label_type={field.label_type}
                                  labelClass="mb-3 focus-z-index position-relative"
                                  control={control}
                                  rules={{
                                    required: `${field.label_name} is required`,
                                  }}
                                  isRequired={field.is_required === "yes"}
                                  isImportant={field.is_important === "yes"}
                                />
                              </Col>
                            )}
                            {field.label_type === "time" && (
                              <Col md={6}>
                                <Time
                                  name={field.id}
                                  label_name={field.label_name}
                                  label_type={field.label_type}
                                  labelClass="mb-3 focus-z-index position-relative"
                                  control={control}
                                  isRequired={field.is_required === "yes"}
                                  isImportant={
                                    field.is_important === "yes"
                                  }
                                />
                              </Col>
                            )}
                            {field.label_type === "time-range" && (
                              <Col md={6}>
                                <Time
                                  name={field.id}
                                  label_name={field.label_name}
                                  label_type={field.label_type}
                                  labelClass="mb-3 focus-z-index position-relative"
                                  control={control}
                                  // rules={{
                                  //   required:
                                  //     field.is_required === "yes" &&
                                  //     "Working hours are required",
                                  //   validate: {
                                  //     validRange: (value) => {
                                  //       if (!value?.start || !value?.end)
                                  //         return true;
                                  //       return (
                                  //         value.start < value.end ||
                                  //         "End time must be after start time"
                                  //       );
                                  //     },
                                  //   },
                                  // }}
                                  isRequired={field.is_required === "yes"}
                                  isImportant={
                                    field.is_important === "yes"
                                  }
                                />
                              </Col>
                            )}

                            {field.label_type === "phone" && (
                              <Col md={6}>
                                <PhoneNumberInput
                                  name={field.id}
                                  label_name={field.label_name}
                                  label_type={field.label_type}
                                  labelClass="mb-3 focus-z-index position-relative"
                                  control={control}
                                  rules={{
                                    required:
                                      field.is_required === "yes" &&
                                      "Phone number is required",
                                  }}
                                  isRequired={field.is_required === "yes"}
                                  isImportant={
                                    field.is_important === "yes"
                                  }
                                  validatePhoneNumber={validatePhoneNumber}
                                />
                              </Col>
                            )}
                            {field.label_type === "date" && (
                              <Col md={6}>
                                <DateField
                                  name={field.id}
                                  label_name={field.label_name}
                                  label_type={field.label_type}
                                  labelClass="mb-3 focus-z-index position-relative"
                                  control={control}
                                  rules={{
                                    required:
                                      field.is_required === "yes" &&
                                      "Date is required",
                                  }}
                                  isRequired={field.is_required === "yes"}
                                  isImportant={
                                    field.is_important === "yes"
                                  }
                                />
                              </Col>
                            )}
                            {field.label_type === "date range" && (
                              <Col md={6}>
                                <DateField
                                  name={field.id}
                                  label_name={field.label_name}
                                  label_type={field.label_type}
                                  labelClass="mb-3 focus-z-index position-relative"
                                  control={control}
                                  // rules={{
                                  //   required:
                                  //     field.is_required === "yes" &&
                                  //     "Date Range number is required",
                                  //   validate: {
                                  //     validRange: (value) => {
                                  //       if (!value?.start || !value?.end)
                                  //         return true;
                                  //       return (
                                  //         value.start <= value.end ||
                                  //         "End date must be after or same as start date"
                                  //       );
                                  //     },
                                  //   },
                                  // }}
                                  isRequired={field.is_required === "yes"}
                                  isImportant={
                                    field.is_important === "yes"
                                  }
                                />
                              </Col>
                            )}
                          </React.Fragment>
                        );
                      })}
                  </Row>
                </div>

                {/* Form Actions */}
                <div
                  style={{
                    position: "fixed",
                    width: "60%",
                    bottom: 0,
                    height: "80px",
                    zIndex: 50,
                    right: 0,
                    borderTop: "1px solid #e0eaff",
                    backgroundColor: "white",
                    paddingRight: "20px",
                  }}
                >
                  <div className="d-flex justify-content-end gap-2 pt-4">
                    <Button
                      onClick={handleClose}
                      className="btn btn-default-outline d-flex"
                      style={{ height: 36, width: "90px" }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="btn btn-default"
                      style={{ height: 36, width: "90px" }}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </Form>
            </div>
          </Offcanvas.Body>
        </Offcanvas>
      </div>
    </div>
  );
};
